import {
  Accordion,
  cn,
  Icon,
  IconButton,
  Text,
} from '@risksmart-app/atomic-ui';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';

import { DetailsSection } from './parts/DetailsSection';
import { RatingBadge } from './parts/RatingBadge';
import { RatingRow } from './parts/RatingRow';
import type { RiskOverviewPanelViewProps } from './types';

const Indicator = ({ color }: { color: string | undefined }) => (
  <span
    className={cn('w-0.5 h-4 rounded-full shrink-0', !color && 'bg-muted')}
    style={
      color
        ? { backgroundColor: getColorStyles(color).backgroundColor }
        : undefined
    }
  />
);

const RiskOverviewPanelView = ({
  lang,
  state,
  actions,
}: RiskOverviewPanelViewProps) => {
  const { ratings, details } = state;

  return (
    <div
      className={cn(
        'min-w-[268px] bg-neutral border border-solid border-neutral-border rounded-lg'
      )}
    >
      <div className={'border-b border-solid border-neutral-border'}>
        <div className={'flex items-center justify-between h-[53px] mx-3'}>
          <Text preset={'heading-sm'} className={'text-lg'}>
            {lang.title}
          </Text>
          <IconButton
            onClick={actions?.onPreferencesClick}
            aria-label={lang.preferencesAriaLabel}
          >
            <Icon name={'dots-vertical'} size={'sm'} />
          </IconButton>
        </div>
      </div>
      <div className={'border-b border-solid border-neutral-border'}>
        <DetailsSection
          lang={lang.details}
          data={details.data}
          loading={details.loading}
        />
      </div>
      <div>
        {/* Remount when the visible-section set changes so the uncontrolled
            Accordion picks up the new defaultValue (re-enabled sections
            otherwise stay collapsed). */}
        <Accordion
          key={ratings.data.map((s) => s.id).join(',')}
          multiple
          defaultValue={ratings.data.map((s) => s.id)}
          className={'gap-0'}
        >
          {ratings.data.map((section, index) => (
            <div
              className={cn(
                index < ratings.data.length - 1 &&
                  'border-b border-solid border-neutral-border'
              )}
              key={section.id}
              data-testid={`${section.id}RatingItem`}
            >
              <Accordion.Item
                value={section.id}
                className={'border-none group/section'}
              >
                <Accordion.Header className={'border-none'}>
                  <Accordion.Trigger
                    className={
                      'bg-neutral p-0 mx-3 min-h-[43px] items-start pt-2'
                    }
                  >
                    <div className={'flex flex-col'}>
                      <div className={'flex items-center gap-2'}>
                        <Indicator color={section.overallRating?.color} />
                        <span
                          className={'text-base font-semibold'}
                          data-testid={'rating-title'}
                        >
                          {section.title}
                        </span>
                        <RatingBadge
                          rating={section.overallRating}
                          displayValue={section.score ?? null}
                          notRatedLabel={lang.ratings.notRatedBadge}
                          className={'px-1.5 py-0.5 text-sm font-normal'}
                          testId={'badge'}
                        />
                        {section.adornment}
                      </div>
                      {section.overallRating === undefined ? (
                        <p
                          className={
                            'text-muted-foreground text-xs font-normal hidden group-data-[closed]/section:block my-0'
                          }
                          data-testid={'rating-no-rating-available'}
                        >
                          {lang.ratings.noRatingAvailable}
                        </p>
                      ) : (
                        section.formattedDate && (
                          <p
                            className={
                              'text-muted-foreground text-xs font-normal hidden group-data-[closed]/section:block my-0'
                            }
                          >
                            {`${lang.ratings.resultDatePrefix} `}
                            <span data-testid={'rating-date'}>
                              {section.formattedDate}
                            </span>
                          </p>
                        )
                      )}
                    </div>
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content className={'p-0 mx-3 mb-4'}>
                  <div className={'flex flex-col gap-3'}>
                    {section.rows.map((row) => (
                      <RatingRow
                        key={row.label}
                        data={row}
                        lang={lang.ratings}
                      />
                    ))}
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            </div>
          ))}
        </Accordion>
      </div>
    </div>
  );
};

export { RiskOverviewPanelView };
